import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandItem, CommandList, CommandGroup } from "@/components/ui/command";
import { Check, Plus, X } from "lucide-react";

/**
 * Medicine picker that searches BOTH trade name and salt combination.
 * Selecting sets the trade name and its salt. Each option shows both so the
 * right medicine can be identified before selecting.
 */
export function MedicineSearch({ value, salt, catalog = [], onSelect, onAdd, onClear, fontSize = 9.5, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = q
    ? catalog.filter((m) => m.trade.toLowerCase().includes(q) || (m.salt || "").toLowerCase().includes(q))
    : catalog;
  const exists = catalog.some((m) => m.trade.toLowerCase() === q);
  const saltSize = Math.max(fontSize - 3, 6);

  const pick = (med) => {
    onSelect(med || {});
    setOpen(false);
    setQuery("");
  };

  const clear = () => {
    if (onClear) onClear();
    else onSelect({ trade: "", salt: "" });
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div data-testid={testid} className="block w-full px-2 py-1 cursor-pointer hover:bg-sky-50 rounded-sm leading-tight" style={{ minHeight: "1.4em" }}>
          <div style={{ fontSize: `${fontSize}pt`, fontWeight: 700 }}>{value || ""}</div>
          {salt ? <div className="text-slate-500" style={{ fontSize: `${saltSize}pt`, lineHeight: 1 }}>{salt}</div> : null}
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-80 bg-white border shadow-lg z-50" data-testid={`${testid}-popover`}>
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search trade name or salt..."
            data-testid={`${testid}-search`}
            onKeyDown={(e) => {
              if (e.key === "Enter" && query.trim()) {
                e.preventDefault();
                pick({ trade: query.trim() });
              }
            }}
          />
          <CommandList className="max-h-64">
            {value ? (
              <CommandGroup>
                <CommandItem value="__clear" onSelect={clear} className="text-slate-500" data-testid={`${testid}-clear`}>
                  <X className="mr-2 h-4 w-4" /> Clear
                </CommandItem>
              </CommandGroup>
            ) : null}
            {query.trim() && !exists && (
              <CommandGroup heading="Free text">
                <CommandItem value={`use-${q}`} onSelect={() => pick({ trade: query.trim() })} data-testid={`${testid}-use`}>
                  <Check className="mr-2 h-4 w-4 text-sky-600" /> Use “{query.trim()}”
                </CommandItem>
                {onAdd && (
                  <CommandItem value={`add-${q}`} onSelect={() => { onAdd(query.trim()); pick({ trade: query.trim() }); }} data-testid={`${testid}-add`}>
                    <Plus className="mr-2 h-4 w-4 text-sky-600" /> Add “{query.trim()}” to medicine list
                  </CommandItem>
                )}
              </CommandGroup>
            )}
            <CommandGroup heading="Medicines">
              {filtered.map((m, idx) => (
                <CommandItem key={`${m.trade}-${idx}`} value={`${m.trade} ${m.salt}`}
                  className="flex flex-col items-start gap-0.5" onSelect={() => pick(m)} data-testid={`${testid}-opt-${idx}`}>
                  <span className="font-medium text-slate-800 flex items-center">
                    {value === m.trade && <Check className="mr-1.5 h-3.5 w-3.5 text-sky-600" />}{m.trade}
                  </span>
                  {m.salt ? <span className="text-xs text-slate-500">{m.salt}</span> : null}
                </CommandItem>
              ))}
              {filtered.length === 0 && !query.trim() && (
                <div className="px-3 py-2 text-xs text-slate-400">Type to search medicines…</div>
              )}
              {filtered.length === 0 && query.trim() && exists === false && (
                <div className="px-3 py-2 text-xs text-slate-400">No match — use free text above.</div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
